import { useEffect, useState } from "react";
import { CSVLink } from "react-csv";
import { Toaster } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import * as Icons from "heroicons-react";
import { ProgressBar } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import {
  resetImport,
  selectfaildData,
  selectprocessedData,
} from "../../features/importSlice";
import {
  useAddImportGroupMutation,
  useGroupListQuery,
} from "../../services/groupApi";
import CsvImporterGroup from "../Common/CsvImporter/CsvImporterGroup";
import Header from "../Common/Header/Header";
import SideBar from "../Common/SideBar/SideBar";
import { signInUser } from "../Utility/Auth";
import { notify } from "../Utility/Notify";
const BASE_URL =
  (process.env.REACT_APP_API_URL || "http://localhost:5001/api/").replace(
    /\/+$/,
    ""
  ) + "/";

const ImportGroup = () => {
  const navigate = useNavigate();
  const user = signInUser() || {};
  const { aamarId } = user;
  const dispatch = useDispatch();
  const data = useSelector((state) => state.importReducer);
  const csvData = data?.csvData;
  const processedData = data?.processedData;
  const faildData = data?.faildData;
  // console.log("processedData", processedData);
  const [isProcess, setIsProcess] = useState(false); // Success Data
  const [pp, setPp] = useState(0); // Process Percent

  const [isImportedDone, setIsImportedDone] = useState(false);
  const [addImportGroup] = useAddImportGroupMutation();

  const { data: groups } = useGroupListQuery(aamarId);

  // Function to handle input change
  const handleChange = (e, code) => {
    const match = processedData.find((p) => p.code === code);
    const rest = processedData.filter((p) => p.code !== code);
    const updated = {
      ...match,
      name: e,
      isNameUnique: checkGroupName(e) || false,
    };
    dispatch(selectprocessedData([...rest, updated]));
  };

  const handleImport = async () => {
    // console.log("Import", csvData.length, "groups");
    setIsImportedDone(true);

    let newProcessData = processedData;
    let newFailedData = [];
    for (const group of processedData) {
      //  TODO:: group name
      const isDuplicate = await checkNameExists(group.name);
      // console.log(isDuplicate, "isDuplicate");

      if (isDuplicate) {
        newFailedData = [
          ...newFailedData,
          {
            name: group?.name,
            // aamarId: aamarId,
            photo: "",
            details: group?.details,
            code: group?.code,
            status: group?.status || "active",
            remarks: "group Import Failed",
          },
        ];
        notify(`group Import ${group?.name}`, "error");
        continue;
      } else {
        if (group?.name) {
          //TODO:: Create each group
          const result = await addImportGroup(group);

          // RTK Query mutation returns { data } on success or { error } on failure
          if (!result?.error) {
            const matched = newProcessData.find((p) => p.name === group?.name);
            const rest = newProcessData.filter((p) => p.name !== group?.name);
            notify(`group Import ${group?.name}`, "success");

            newProcessData = [...rest, { ...matched, isImport: true }];
          } else {
            newFailedData = [
              ...newFailedData,
              {
                name: group?.name,
                // aamarId: aamarId,
                details: group?.details,
                code: group?.code,
                status: group?.status || "active",
                remarks: "Group Import Failed",
              },
            ];
            notify(`group Import ${group?.name}`, "error");
          }
        } else {
          newFailedData = [
            ...newFailedData,
            {
              name: group?.name,
              // aamarId: aamarId,
              details: group?.details,
              code: group?.code,
              status: group?.status || "active",
              remarks: "group Import Failed",
            },
          ];
          notify(`group Import ${group?.name}`, "error");
        }
      }
    }
    dispatch(selectfaildData(newFailedData));
    dispatch(selectprocessedData(newProcessData));
  };

  // check Name
  const checkNameExists = async (name) => {
    try {
      const response = await fetch(
        `${BASE_URL}group/checkName/${aamarId}/${encodeURIComponent(name || "")}`
      );
      if (response.ok) {
        const data = await response.json();
        return !!data.exists;
      }
    } catch (error) {
      console.error("Error checking Group name:", error);
    }
    // Fallback to local group check if API fails
    return (
      groups?.some(
        (group) =>
          String(group?.name || "").trim().toLowerCase() ===
          String(name || "").trim().toLowerCase()
      ) || false
    );
  };

  //TODO:: Generate Group Code
  const generateCode = async () => {
    try {
      let code;
      let isUnique = false;
      let attempts = 0;

      while (!isUnique && attempts < 100) {
        attempts++;
        code = uuidv4().replace(/\D/g, "").slice(0, 6);

        // Ensure the code has 6 digits and does not start with 0
        if (code.length < 6 || code.startsWith("0")) {
          continue;
        }

        isUnique = !(await checkCodeExists(code));
      }

      return code;
    } catch (error) {
      console.error("Error generating Group Code:", error);
      return null;
    }
  };

  const processCsvData = async (incomingCsvData) => {
    if (!incomingCsvData || incomingCsvData.length === 0) return;
    dispatch(selectprocessedData([]));
    dispatch(selectfaildData([]));
    setIsProcess(false);
    setPp(0);

    let oldData = [];
    let i = 1;

    for (const group of incomingCsvData) {
      const generatedCode = (await generateCode()) || null;
      const isUnique = checkGroupName(group?.name);

      const newGroup = {
        name: group?.name,
        aamarId: aamarId,
        photo: "",
        details: group?.details || "",
        code: generatedCode,
        status: group?.status || "active",
        order: i++,
        readyToImport: true,
        isImport: false,
        isEditable: isUnique,
        isNameUnique: isUnique,
      };

      oldData = [...oldData, newGroup];
    }

    dispatch(selectprocessedData(oldData));
  };

  useEffect(() => {
    if (csvData?.length > 0) {
      processCsvData(csvData);
    }
  }, [csvData]);

  useEffect(() => {
    return () => {
      dispatch(resetImport());
    };
  }, [dispatch]);

  useEffect(() => {
    if (processedData && processedData.length > 0) {
      setIsProcess(true);
      const total = csvData?.length || processedData.length;
      const processPercent = Math.round((100 / total) * processedData.length);
      setPp(processPercent || 100);
    }
  }, [processedData, csvData]);

  const checkGroupName = (name) => {
    if (!name) return false;
    return (
      !groups?.some(
        (group) =>
          String(group?.name || "").trim().toLowerCase() ===
          String(name || "").trim().toLowerCase()
      ) || false
    );
  };

  // check code
  const checkCodeExists = async (code) => {
    if (!code) return false;
    return (
      groups?.some(
        (group) =>
          String(group?.code || "").trim().toLowerCase() ===
          String(code || "").trim().toLowerCase()
      ) || false
    );
  };

  //   console.log("PP", pp);
  return (
    <div>
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-2">
            <SideBar />
          </div>
          <div className="col-md-10">
            <Header title="Import Group" />
            <div className="row">
              <div className="col-md-12 table-responsive">
                {csvData?.length <= 0 ? (
                  <div
                    className="d-flex align-items-center justify-content-center vh-100"
                    style={{ marginTop: "-80px" }}
                  >
                    <div className="col-6">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <p className="m-0 p-0 fw-bold">Import Groups</p>
                          <p className="m-0 p-0">
                            <small>
                              Please check the format before uploading
                              <a
                                href="/importSample/groupImportSample.csv"
                                download
                                className="d-inline-flex align-items-center text-decoration-none text-opacity-75 text-primary gap-1"
                                style={{ transition: "color 0.3s ease-in-out" }}
                              >
                                <Icons.Download size="15" />
                                Download
                              </a>{" "}
                              the sample file
                              {/* File to check the format before uploading. */}
                            </small>
                          </p>
                        </div>
                      </div>
                      <div>
                        <CsvImporterGroup />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="row">
                    <div className="col-12">
                      <p className="d-flex justify-content-between p-2">
                        Total group: {csvData?.length}{" "}
                        <span className="input-group-text">
                          <span
                            className="input-group-text gap-2"
                            id="basic-addon1"
                          >
                            {isImportedDone && faildData?.length > 0 && (
                              // TODO:: MAKE THIS BUTTON EXPOrt FAILD

                              <CSVLink
                                data={faildData}
                                filename="failed_groups.csv"
                                className="btn btn-dark"
                              >
                                <Icons.Download size="18" /> Export Failed Data
                              </CSVLink>
                            )}
                            {!isImportedDone && (
                              <button
                                onClick={() => handleImport()}
                                className="btn btn-block btn-dark"
                                style={{ zIndex: 0 }}
                              >
                                <Icons.Upload size="18" /> Import groups
                              </button>
                            )}
                            {!isImportedDone && (
                              <button
                                onClick={() => {
                                  processCsvData(csvData);
                                  setIsImportedDone(false);
                                }}
                                className="btn btn-block btn-outline-dark"
                                style={{ zIndex: 0 }}
                              >
                                <Icons.Refresh size="18" /> Reload
                              </button>
                            )}

                            <button
                              onClick={() => {
                                dispatch(resetImport());
                                setIsImportedDone(false);
                              }}
                              className="btn btn-block btn-outline-dark"
                              style={{ zIndex: 0 }}
                            >
                              {!isImportedDone ? "Cancel" : "Import Again"}{" "}
                              <Icons.X size="18" />
                            </button>
                            {isImportedDone && (
                              <button
                                onClick={() => {
                                  dispatch(resetImport());
                                  setIsImportedDone(false);
                                  navigate("/group");
                                }}
                                className="btn btn-block btn-outline-success"
                                style={{ zIndex: 0 }}
                              >
                                <Icons.CheckCircle size="18" /> Done
                              </button>
                            )}
                          </span>
                        </span>
                      </p>
                    </div>
                    {!isProcess ? (
                      <div className="col-12 col-md-6 offset-md-3">
                        <div
                          className="d-flex flex-column align-items-left justify-content-center w-100"
                          style={{ height: "60vh" }}
                        >
                          <h3>Processing Data {pp}%</h3>
                          <div className="w-100">
                            <ProgressBar
                              animated
                              striped
                              variant="success"
                              now={pp}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="col-12">
                        <table className="table table-striped">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Name</th>
                              <th>Code</th>
                              <th>Details</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {processedData.length > 0 ? (
                              processedData
                                ?.slice()
                                ?.sort((a, b) => a.order - b.order)
                                ?.map((item, index) => (
                                  <tr key={index}>
                                    <th>{index + 1}</th>
                                    <td>
                                      {item?.isEditable ? (
                                        //     <input
                                        //     className="form-control"
                                        //     value={item?.name}
                                        //     onChange={(e) => handleChange(e.target.value,item?.code)} // If needed for editing
                                        //     style={{
                                        //     //   border: "1px solid",
                                        //       outline: "none",
                                        //       width: "200px", // Adjust the width as needed
                                        //       padding: "5px", // To ensure text fits comfortably
                                        //     }}
                                        //   />
                                        item?.name
                                      ) : (
                                        <input
                                          className="form-control"
                                          value={item?.name}
                                          onChange={(e) =>
                                            handleChange(
                                              e.target.value,
                                              item?.code
                                            )
                                          } // If needed for editing
                                          style={{
                                            border: `1px solid ${
                                              item?.isNameUnique ? "" : "tomato"
                                            }`,
                                            outline: "none",
                                            width: "200px", // Adjust the width as needed
                                            padding: "5px", // To ensure text fits comfortably
                                          }}
                                        />
                                      )}
                                    </td>

                                    <td>{item?.code}</td>
                                    <td>{item?.details}</td>
                                    <td
                                      className={
                                        item?.isImport
                                          ? "text-success"
                                          : item?.isNameUnique
                                          ? "text-success"
                                          : "text-danger"
                                      }
                                    >
                                      {item?.isImport
                                        ? "Done"
                                        : item?.isNameUnique
                                        ? "Ready"
                                        : "Failed"}
                                    </td>
                                  </tr>
                                ))
                            ) : (
                              <tr>
                                <td colSpan="15" className="text-center">
                                  No Data Available
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <Toaster position="bottom-right" />
        </div>
      </div>
    </div>
  );
};

export default ImportGroup;
